import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  TrendingUp, 
  Target, 
  Zap, 
  Shield, 
  Crown, 
  Rocket,
  DollarSign,
  Calendar,
  BarChart3,
  Award,
  Star,
  ArrowUp,
  ArrowDown,
  Timer,
  Sparkles,
  Trophy,
  Flame
} from 'lucide-react';

interface DataPoint {
  annee: number;
  conservateur: number;
  equilibre: number;
  dynamique: number;
  capitalInvesti: number;
}

type StrategieType = 'conservateur' | 'equilibre' | 'dynamique';

interface Strategie {
  id: StrategieType;
  nom: string;
  slogan: string;
  rendement: number;
  risque: 'Faible' | 'Modéré' | 'Élevé';
  icon: React.ReactNode;
  couleur: string;
  couleurBg: string;
  couleurGradient: string;
  description: string;
  allocation: {
    name: string;
    value: number;
    color: string;
  }[];
  avantages: string[];
  profil: string;
}

const strategies: Strategie[] = [
  {
    id: 'conservateur',
    nom: 'Protecteur',
    slogan: 'Sécurisez votre avenir',
    rendement: 0.035,
    risque: 'Faible',
    icon: <Shield className="w-8 h-8 text-white" />,
    couleur: 'from-green-500 to-emerald-600',
    couleurBg: 'from-green-50 to-emerald-50',
    couleurGradient: 'bg-gradient-to-br from-green-400 to-emerald-600',
    description: 'Stratégie sécurisée privilégiant la préservation du capital avec des revenus réguliers',
    allocation: [
      { name: 'Sukuk', value: 60, color: '#10b981' },
      { name: 'SCPI', value: 35, color: '#059669' },
      { name: 'GFI', value: 5, color: '#34d399' }
    ],
    avantages: ['Capital protégé', 'Revenus stables', 'Faible volatilité'],
    profil: 'Investisseur prudent recherchant la sécurité'
  },
  {
    id: 'equilibre',
    nom: 'Équilibré',
    slogan: 'Le parfait compromis',
    rendement: 0.055,
    risque: 'Modéré',
    icon: <Target className="w-8 h-8 text-white" />,
    couleur: 'from-blue-500 to-indigo-600',
    couleurBg: 'from-blue-50 to-indigo-50',
    couleurGradient: 'bg-gradient-to-br from-blue-400 to-indigo-600',
    description: 'Équilibre optimal entre sécurité et performance pour un rendement attractif',
    allocation: [
      { name: 'SCPI', value: 45, color: '#3b82f6' },
      { name: 'GFI', value: 35, color: '#1d4ed8' },
      { name: 'Sukuk', value: 20, color: '#60a5fa' }
    ],
    avantages: ['Croissance modérée', 'Risque maîtrisé', 'Diversification'],
    profil: 'Investisseur équilibré visant croissance et stabilité'
  },
  {
    id: 'dynamique',
    nom: 'Conquérant',
    slogan: 'Maximisez vos gains',
    rendement: 0.085,
    risque: 'Élevé',
    icon: <Rocket className="w-8 h-8 text-white" />,
    couleur: 'from-purple-500 to-pink-600',
    couleurBg: 'from-purple-50 to-pink-50',
    couleurGradient: 'bg-gradient-to-br from-purple-500 to-pink-600',
    description: 'Stratégie agressive maximisant le potentiel de croissance sur le long terme',
    allocation: [
      { name: 'GFI', value: 60, color: '#8b5cf6' },
      { name: 'SCPI', value: 30, color: '#a855f7' },
      { name: 'Sukuk', value: 10, color: '#c084fc' }
    ],
    avantages: ['Potentiel élevé', 'Croissance forte', 'Innovation'],
    profil: 'Investisseur ambitieux acceptant la volatilité'
  }
];

const SimulateurStrategies: React.FC = () => {
  const [strategieSelectionnee, setStrategieSelectionnee] = useState<StrategieType>('equilibre');
  const [capitalInitial, setCapitalInitial] = useState<number>(25000);
  const [versementMensuel, setVersementMensuel] = useState<number>(500);
  const [dureeAnnees, setDureeAnnees] = useState<number>(15);
  const [data, setData] = useState<DataPoint[]>([]);
  const [showComparaison, setShowComparaison] = useState<boolean>(true);

  const strategie = strategies.find(s => s.id === strategieSelectionnee)!;

  const calculerProjections = () => {
    const nouvelleDonnee: DataPoint[] = [];
    
    for (let annee = 0; annee <= dureeAnnees; annee++) {
      const capitalTotalInvesti = capitalInitial + (versementMensuel * 12 * annee);
      
      // Calculs pour chaque stratégie
      const valeurs = strategies.reduce((acc, strat) => {
        let valeur = capitalInitial * Math.pow(1 + strat.rendement, annee);
        
        if (annee > 0) {
          const versementAnnuel = versementMensuel * 12;
          const valeurVersements = versementAnnuel * ((Math.pow(1 + strat.rendement, annee) - 1) / strat.rendement);
          valeur += valeurVersements;
        }
        
        acc[strat.id] = Math.round(valeur);
        return acc;
      }, {} as Record<StrategieType, number>);
      
      nouvelleDonnee.push({
        annee,
        conservateur: valeurs.conservateur,
        equilibre: valeurs.equilibre,
        dynamique: valeurs.dynamique,
        capitalInvesti: Math.round(capitalTotalInvesti)
      });
    }
    
    setData(nouvelleDonnee);
  };

  useEffect(() => {
    calculerProjections();
  }, [capitalInitial, versementMensuel, dureeAnnees]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const resultatsFinaux = data[data.length - 1];
  const valeurFinale = resultatsFinaux ? resultatsFinaux[strategieSelectionnee] : 0;
  const totalInvesti = resultatsFinaux ? resultatsFinaux.capitalInvesti : capitalInitial;
  const gainTotal = valeurFinale - totalInvesti;
  const rendementTotal = totalInvesti > 0 ? ((valeurFinale / totalInvesti - 1) * 100) : 0;

  // Calcul des gains par rapport aux autres stratégies
  const gainVsConservateur = resultatsFinaux ? resultatsFinaux[strategieSelectionnee] - resultatsFinaux.conservateur : 0;
  const gainVsEquilibre = resultatsFinaux ? resultatsFinaux[strategieSelectionnee] - resultatsFinaux.equilibre : 0;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-white border border-gray-200 shadow-sm text-gray-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <div className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></div>
            <Sparkles className="w-4 h-4 mr-2 text-orange-500" />
            Simulateur de Stratégies d'Investissement
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            Trouvez votre{' '}
            <span style={{ 
              background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}>
              Stratégie Gagnante
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light">
            🚀 Découvrez votre potentiel d'investissement avec nos stratégies sur-mesure et visualisations dynamiques
          </p>
        </div>

        {/* Sélection de Stratégie */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Choisissez votre profil d'investisseur</h2>
            <p className="text-gray-600">Chaque stratégie est optimisée pour un profil spécifique</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {strategies.map((strat, index) => (
              <div
                key={strat.id}
                onClick={() => setStrategieSelectionnee(strat.id)}
                className={`cursor-pointer rounded-3xl p-8 border-2 transition-all duration-500 hover:shadow-2xl transform hover:scale-105 ${
                  strategieSelectionnee === strat.id
                    ? 'border-orange-500 shadow-2xl scale-105 ring-4 ring-orange-200'
                    : 'border-gray-200 hover:border-gray-300 shadow-lg'
                }`}
                style={{
                  background: strategieSelectionnee === strat.id
                    ? `linear-gradient(135deg, ${strat.couleurBg.split(' ')[1]} 0%, ${strat.couleurBg.split(' ')[3]} 100%)`
                    : 'white'
                }}
              >
                {/* Badge position */}
                <div className="flex justify-between items-start mb-6">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${strat.couleurGradient}`}>
                    {strat.icon}
                  </div>
                  {strategieSelectionnee === strat.id && (
                    <div className="flex items-center space-x-1">
                      <Trophy className="w-5 h-5 text-orange-500" />
                      <span className="text-sm font-bold text-orange-600">SÉLECTIONNÉ</span>
                    </div>
                  )}
                  {index === 1 && strategieSelectionnee !== strat.id && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                      RECOMMANDÉ
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{strat.nom}</h3>
                    <p className="text-sm font-medium text-gray-600 italic">"{strat.slogan}"</p>
                  </div>
                  
                  <p className="text-sm text-gray-700 leading-relaxed">{strat.description}</p>
                  
                  {/* Métriques clés */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-white/50 rounded-xl">
                      <div className="text-2xl font-bold" style={{ color: strat.couleur.split(' ')[1] }}>
                        {formatPercentage(strat.rendement)}
                      </div>
                      <div className="text-xs text-gray-600">Rendement cible</div>
                    </div>
                    <div className="text-center p-3 bg-white/50 rounded-xl">
                      <div className={`text-sm font-bold px-2 py-1 rounded-full ${
                        strat.risque === 'Faible' ? 'bg-green-100 text-green-700' :
                        strat.risque === 'Modéré' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {strat.risque}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Niveau de risque</div>
                    </div>
                  </div>

                  {/* Profil cible */}
                  <div className="bg-white/30 rounded-lg p-3 border border-white/50">
                    <div className="text-xs font-semibold text-gray-700 mb-1">💡 Profil idéal :</div>
                    <div className="text-xs text-gray-600">{strat.profil}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Layout: Paramètres et résultats */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 mb-8">
          {/* Paramètres */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
              <div className="border-b border-gray-100 pb-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${strategie.couleurGradient}`}>
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Vos Paramètres</h2>
                    <p className="text-xs text-gray-500">Stratégie {strategie.nom}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Capital Initial */}
                <div className={`p-4 rounded-2xl border bg-gradient-to-r ${strategie.couleurBg}`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <DollarSign className="w-5 h-5" style={{ color: strategie.couleur.split(' ')[1] }} />
                    <label className="text-sm font-bold text-gray-800">Capital de Départ</label>
                  </div>
                  <input
                    type="range"
                    min="5000"
                    max="200000"
                    step="5000"
                    value={capitalInitial}
                    onChange={(e) => setCapitalInitial(Number(e.target.value))}
                    className="w-full h-3 bg-white/50 rounded-lg appearance-none cursor-pointer mb-3"
                  />
                  <input
                    type="number"
                    value={capitalInitial}
                    onChange={(e) => setCapitalInitial(Number(e.target.value))}
                    className="w-full px-4 py-3 border-0 rounded-xl bg-white shadow-sm text-center font-bold text-lg"
                  />
                  <div className="text-center mt-2">
                    <div className="text-2xl font-bold" style={{ color: strategie.couleur.split(' ')[1] }}>{formatCurrency(capitalInitial)}</div>
                  </div>
                </div>

                {/* Versement Mensuel */}
                <div className={`p-4 rounded-2xl border bg-gradient-to-r ${strategie.couleurBg}`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <Calendar className="w-5 h-5" style={{ color: strategie.couleur.split(' ')[1] }} />
                    <label className="text-sm font-bold text-gray-800">Épargne Mensuelle</label>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="2000"
                    step="100"
                    value={versementMensuel}
                    onChange={(e) => setVersementMensuel(Number(e.target.value))}
                    className="w-full h-3 bg-white/50 rounded-lg appearance-none cursor-pointer mb-3"
                  />
                  <input
                    type="number"
                    value={versementMensuel}
                    onChange={(e) => setVersementMensuel(Number(e.target.value))}
                    className="w-full px-4 py-3 border-0 rounded-xl bg-white shadow-sm text-center font-bold text-lg"
                  />
                  <div className="text-center mt-2">
                    <div className="text-2xl font-bold" style={{ color: strategie.couleur.split(' ')[1] }}>{formatCurrency(versementMensuel)}</div>
                    <div className="text-xs text-gray-600">par mois</div>
                  </div>
                </div>

                {/* Durée */}
                <div className={`p-4 rounded-2xl border bg-gradient-to-r ${strategie.couleurBg}`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <Timer className="w-5 h-5" style={{ color: strategie.couleur.split(' ')[1] }} />
                    <label className="text-sm font-bold text-gray-800">Horizon d'Investissement</label>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="30"
                    step="1"
                    value={dureeAnnees}
                    onChange={(e) => setDureeAnnees(Number(e.target.value))}
                    className="w-full h-3 bg-white/50 rounded-lg appearance-none cursor-pointer mb-3"
                  />
                  <input
                    type="number"
                    value={dureeAnnees}
                    onChange={(e) => setDureeAnnees(Number(e.target.value))}
                    className="w-full px-4 py-3 border-0 rounded-xl bg-white shadow-sm text-center font-bold text-lg"
                  />
                  <div className="text-center mt-2">
                    <div className="text-2xl font-bold" style={{ color: strategie.couleur.split(' ')[1] }}>{dureeAnnees}</div>
                    <div className="text-xs text-gray-600">années</div>
                  </div>
                </div>

                {/* Toggle comparaison */}
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-2xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <BarChart3 className="w-5 h-5 text-gray-600" />
                      <div>
                        <div className="text-sm font-bold text-gray-800">Comparaison</div>
                        <div className="text-xs text-gray-500">Afficher toutes les stratégies</div>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showComparaison}
                        onChange={(e) => setShowComparaison(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-7 w-12 rounded-full transition-all duration-300 ${
                        showComparaison 
                          ? `${strategie.couleurGradient} shadow-lg` 
                          : 'bg-gray-300'
                      }`}>
                        <div className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-2 ring-white transition-all duration-300 ${
                          showComparaison ? 'translate-x-6 mt-1' : 'translate-x-1 mt-1'
                        }`} />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Résultats et Graphiques */}
          <div className="xl:col-span-3 space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Valeur finale */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className={`absolute top-0 left-0 w-full h-1 ${strategie.couleurGradient}`}></div>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${strategie.couleurGradient}`}>
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center space-x-1">
                    <ArrowUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-bold text-green-600">+{rendementTotal.toFixed(1)}%</span>
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Valeur Finale</h3>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {formatCurrency(valeurFinale)}
                </p>
                <div className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-full inline-block">
                  +{formatCurrency(gainTotal)} de gains
                </div>
              </div>

              {/* ROI */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full font-bold">
                    {formatPercentage(strategie.rendement)} / an
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Rendement Total</h3>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {rendementTotal.toFixed(1)}%
                </p>
                <div className="text-xs text-blue-600 font-medium">
                  Sur {dureeAnnees} années
                </div>
              </div>

              {/* Avantage vs Conservateur */}
              {strategieSelectionnee !== 'conservateur' && (
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-500"></div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <Flame className="w-5 h-5 text-orange-500" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Gain vs Protecteur</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-1">
                    {formatCurrency(gainVsConservateur)}
                  </p>
                  <div className="text-xs text-green-600 font-bold">
                    +{((gainVsConservateur / resultatsFinaux?.conservateur) * 100 || 0).toFixed(1)}% de plus
                  </div>
                </div>
              )}

              {/* Épargne mensuelle impact */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-purple-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <Award className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Épargne Totale</h3>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(totalInvesti)}
                </p>
                <div className="text-xs text-purple-600 font-medium">
                  {formatCurrency(versementMensuel)} × {dureeAnnees * 12} mois
                </div>
              </div>
            </div>

            {/* Graphiques */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Graphique d'évolution */}
              <div className="lg:col-span-2 bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                <div className="border-b border-gray-100 pb-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${strategie.couleurGradient}`}>
                        <BarChart3 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Évolution de votre Patrimoine</h2>
                        <p className="text-sm text-gray-500 mt-1">Stratégie {strategie.nom} sur {dureeAnnees} ans</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="annee" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '1rem',
                          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value: any, name: string) => [
                          formatCurrency(value),
                          name === 'conservateur' ? '🛡️ Protecteur' :
                          name === 'equilibre' ? '⚖️ Équilibré' :
                          name === 'dynamique' ? '🚀 Conquérant' : 'Capital investi'
                        ]}
                        labelFormatter={(label) => `Année ${label}`}
                      />
                      <Legend />
                      
                      <Line
                        type="monotone"
                        dataKey="capitalInvesti"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="💰 Capital investi"
                        dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                      />
                      
                      {showComparaison ? (
                        <>
                          <Line
                            type="monotone"
                            dataKey="conservateur"
                            stroke="#10b981"
                            strokeWidth={strategieSelectionnee === 'conservateur' ? 4 : 2}
                            name="🛡️ Protecteur"
                            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="equilibre"
                            stroke="#3b82f6"
                            strokeWidth={strategieSelectionnee === 'equilibre' ? 4 : 2}
                            name="⚖️ Équilibré"
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="dynamique"
                            stroke="#8b5cf6"
                            strokeWidth={strategieSelectionnee === 'dynamique' ? 4 : 2}
                            name="🚀 Conquérant"
                            dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                          />
                        </>
                      ) : (
                        <Line
                          type="monotone"
                          dataKey={strategieSelectionnee}
                          stroke={strategieSelectionnee === 'conservateur' ? '#10b981' : 
                                 strategieSelectionnee === 'equilibre' ? '#3b82f6' : '#8b5cf6'}
                          strokeWidth={4}
                          name={`${strategie.icon} ${strategie.nom}`}
                          dot={{ 
                            fill: strategieSelectionnee === 'conservateur' ? '#10b981' : 
                                  strategieSelectionnee === 'equilibre' ? '#3b82f6' : '#8b5cf6', 
                            strokeWidth: 2, r: 5 
                          }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Allocation d'actifs */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                <div className="border-b border-gray-100 pb-4 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Répartition des Actifs</h3>
                  <p className="text-sm text-gray-500">Stratégie {strategie.nom}</p>
                </div>

                <div className="space-y-4 mb-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={strategie.allocation}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {strategie.allocation.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-3">
                  {strategie.allocation.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{item.value}%</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl">
                  <h4 className="text-sm font-bold text-gray-800 mb-3">✨ Avantages clés :</h4>
                  <ul className="space-y-2">
                    {strategie.avantages.map((avantage, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Star className="w-3 h-3 text-yellow-500 mr-2 flex-shrink-0" />
                        {avantage}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-orange-500 via-red-500 to-purple-600 rounded-3xl p-8 text-white text-center shadow-2xl">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center mb-4">
              <div className="flex items-center space-x-2">
                <Rocket className="w-8 h-8" />
                <Sparkles className="w-6 h-6" />
                <Trophy className="w-8 h-8" />
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-4">
              Prêt à transformer votre avenir financier ? 
            </h2>
            <p className="text-xl mb-6 opacity-90">
              Avec la stratégie <strong>{strategie.nom}</strong>, vous pourriez gagner <strong>{formatCurrency(gainTotal)}</strong> en {dureeAnnees} ans !
            </p>
            <div className="flex justify-center">
              <button className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center justify-center space-x-2">
                <Flame className="w-6 h-6" />
                <span>Commencer maintenant</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulateurStrategies;